import { z } from 'zod';

/* ===========================================================================
 * THE BUILDER'S EXCEL EXPORT — THE SERVER FORMATS, IT DOES NOT COMPUTE.
 *
 * Every other report's Excel is `?format=excel` on the endpoint that answers
 * it: the server holds the query, so the server can render the file. The
 * builder is not shaped like that. Its table is COMPOSED IN THE BROWSER —
 * `aggregateColumns` runs over an already-fetched payload, applies the measure
 * catalog's kind rules (a snapshot takes the last day, a ratio is never summed)
 * and produces the rows on screen.
 *
 * So there are two ways to give it an Excel file, and only one of them is
 * allowed here.
 *
 *   1. RE-DERIVE THE FIGURES SERVER-SIDE from the composed question. This is
 *      the obvious design and it is the wrong one: it puts a second
 *      implementation of the measure catalog, the grain roll-up rules and the
 *      cross-cut keying into a second language. The moment the two disagree —
 *      one of them rounds differently, one of them treats a null as zero — the
 *      product ships a spreadsheet that does not match the screen it was
 *      exported from, and the spreadsheet is the artefact that gets reconciled.
 *      That is design-rules §3.2, one fact one source, and it is exactly the
 *      class of defect this codebase has shipped most often.
 *
 *   2. SEND THE ROWS THAT ARE ON SCREEN and have the server lay them out. The
 *      server performs no arithmetic at all: it stamps the standard scope
 *      banner (`buildReportMeta`) and writes the cells it was handed. The file
 *      and the screen cannot disagree, because there is only one calculation.
 *
 * This schema is the contract for (2). Note what it deliberately does NOT
 * carry: no measure ids, no grouping, no grain. Accepting those would invite a
 * future change to "just recompute it properly on the server" and reintroduce
 * (1) without anyone noticing the schema had made room for it.
 *
 * WHAT ABOUT TRUST? The body is the caller's own data going back to the caller
 * as a file — no record is read, written or joined against it, and the scope
 * banner is still built from the SESSION and the query string, never from the
 * body. A caller who lies about their own figures has produced a spreadsheet of
 * their own numbers, which they could have typed. The limits below exist for
 * resource safety, not for trust.
 * ======================================================================== */

/**
 * A cell. `null` is the dash — "no figure for this group" — and it stays a
 * distinct value all the way into the workbook rather than being flattened to
 * an empty string on the way, because §3.3 makes "unknown" and "zero" different
 * claims and the file has to keep saying which one it means.
 */
export const BuilderCellSchema = z.union([z.string().max(500), z.number(), z.null()]);

export const BuilderExportColumnSchema = z.object({
  /** Matches a key on every row object below. */
  key: z.string().min(1).max(120),
  /** The column header, already translated by the client that composed it. */
  header: z.string().min(1).max(200),
  align: z.enum(['left', 'right', 'center']).optional(),
});

/**
 * Caps chosen to bound memory rather than to express a product limit: the
 * builder's own sources return their whole result in one request, so a table
 * larger than this is not something a reader is looking at.
 */
export const BuilderExportSchema = z.object({
  /** The composed sentence. Becomes the workbook title and the file name stem. */
  title: z.string().min(1).max(300),
  columns: z.array(BuilderExportColumnSchema).min(1).max(24),
  rows: z.array(z.record(z.string(), BuilderCellSchema)).max(20000),
  /**
   * The footer, when the table has one. Only columns the builder judged
   * ADDABLE appear in `cells` — a ratio or a distinct count is absent rather
   * than zero, so the workbook reproduces the blank footer cell the reader saw
   * instead of inventing a sum the screen declined to print.
   */
  totals: z
    .object({
      label: z.string().min(1).max(200),
      cells: z.record(z.string(), z.union([z.number(), z.string().max(200)])),
    })
    .optional(),
  /**
   * The provenance lines — what one row covers, what the figures leave out, why
   * a column has no total. They travel with the file for the reason the CSV's
   * basis block does: a spreadsheet carrying the figures and leaving the
   * exclusions behind is reconciled as if it covered everything.
   */
  basis: z.array(z.string().max(1000)).max(20).default([]),
});

export type BuilderExportColumn = z.infer<typeof BuilderExportColumnSchema>;
export type BuilderExportBody = z.infer<typeof BuilderExportSchema>;

/**
 * Emailing the same file, not a different one. `POST /reports/builder/send`
 * builds the IDENTICAL workbook `/builder/export` does — same rows, same
 * columns, same totals, same basis lines — through the same
 * `buildExcelWorkbookWithMeta` call, and attaches it instead of streaming it
 * back. `.extend` rather than a fresh object literal, so the two schemas can
 * never drift on what a row or a column is.
 *
 * `recipients` is capped at 10 for the same resource-safety reason the export
 * caps rows and columns, not because ten is a product limit.
 */
export const BuilderExportEmailSchema = BuilderExportSchema.extend({
  recipients: z.array(z.string().email()).min(1).max(10),
});
export type BuilderExportEmailBody = z.infer<typeof BuilderExportEmailSchema>;
